import { type ChartConfig } from "@/components/ui/chart";
import {
  InsightSentiment,
  BarChartComponent,
  LineChartComponent,
} from "@/types/insights";

export const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
];

export const sentimentBadgeClassNames: Record<InsightSentiment, string> = {
  positive: "border-emerald-200 bg-emerald-50 text-emerald-700",
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
  negative: "border-amber-200 bg-amber-50 text-amber-700",
};

export const metricCardClassNames: Record<InsightSentiment, string> = {
  positive: "border-emerald-200/80 bg-emerald-50/60",
  neutral: "border-slate-200/80 bg-slate-50/60",
  negative: "border-amber-200/80 bg-amber-50/60",
};

export const metricValueClassNames: Record<InsightSentiment, string> = {
  positive: "text-emerald-950",
  neutral: "text-slate-950",
  negative: "text-amber-950",
};

export const formatNumber = (value: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);

const COMPACT_UNITS = new Set(["%", "°", "°C", "°F"]);

export const formatMetricValue = (value: number, unit?: string | null) => {
  const formattedValue = formatNumber(value);
  if (!unit) return formattedValue;
  return COMPACT_UNITS.has(unit) ? `${formattedValue}${unit}` : `${formattedValue} ${unit}`;
};

export const formatMetricDelta = (value: number, unit?: string | null) => {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  const formattedValue = `${sign}${formatNumber(Math.abs(value))}`;
  if (!unit) return formattedValue;
  return COMPACT_UNITS.has(unit) ? `${formattedValue}${unit}` : `${formattedValue} ${unit}`;
};

export const buildLineChartData = (component: LineChartComponent) => {
  const rows = new Map<string, Record<string, number | string>>();

  component.series.forEach((series) => {
    series.points.forEach((point) => {
      const current = rows.get(point.x) ?? { x: point.x };
      current[series.key] = point.y;
      rows.set(point.x, current);
    });
  });

  return Array.from(rows.values());
};

export const getBarChartSeries = (component: BarChartComponent) => {
  const seen = new Map<string, string>();

  component.data.forEach((datum) => {
    const label = datum.series || component.yAxis?.label || "Value";

    if (!seen.has(label)) {
      seen.set(label, `series_${seen.size}`);
    }
  });

  return Array.from(seen.entries()).map(([label, key]) => ({ key, label }));
};

export const buildBarChartData = (
  component: BarChartComponent,
  series: Array<{ key: string; label: string }>
) => {
  const rows = new Map<string, Record<string, number | string>>();
  const seriesKeyByLabel = new Map(series.map((item) => [item.label, item.key]));

  component.data.forEach((datum) => {
    const label = datum.series || component.yAxis?.label || "Value";
    const seriesKey = seriesKeyByLabel.get(label);
    if (!seriesKey) return;

    const current = rows.get(datum.category) ?? { category: datum.category };
    current[seriesKey] = datum.value;
    rows.set(datum.category, current);
  });

  return Array.from(rows.values());
};

export const buildChartConfig = (items: Array<{ key: string; label: string }>): ChartConfig =>
  items.reduce<ChartConfig>((config, item, index) => {
    config[item.key] = {
      label: item.label,
      color: CHART_COLORS[index % CHART_COLORS.length],
    };
    return config;
  }, {});
